import { useEffect, useState } from "react";
import { api } from "./lib/api";
import { store, useStore } from "./lib/store";
import { Login } from "./components/Login";
import { Sidebar } from "./components/Sidebar";
import { ChatView } from "./components/ChatView";
import { SettingsPanel } from "./components/SettingsPanel";
import { Logo } from "./components/Logo";
import { ChatSocket } from "./lib/ws";

interface Config {
  google_client_id: string;
  turnstile_site_key: string;
  jitsi_domain: string;
  app_name: string;
}

export function App() {
  const ready = useStore((s) => s.ready);
  const user = useStore((s) => s.user);
  const activeChatId = useStore((s) => s.activeChatId);
  const [config, setConfig] = useState<Config | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const cfg = await fetch(`${api.origin()}/api/config`).then((r) => r.json()) as Config;
        // /api/config returns google_client_id but may not return turnstile_site_key in prod minimal — patch via env var
        const ts = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || (cfg as { turnstile_site_key?: string }).turnstile_site_key || "";
        setConfig({ ...cfg, turnstile_site_key: ts });

        const t = api.getToken();
        if (t) {
          try {
            const me = await api.me();
            store.set({ user: me.user });
            const s = await api.getSettings();
            store.set({ settings: s.settings });
            const ch = await api.listChats();
            store.set({ chats: ch.chats });
          } catch {
            api.setToken(null);
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        store.set({ ready: true });
      }
    })();
  }, []);

  // Handle deep links: /u/:token or /c/:token
  useEffect(() => {
    if (!user) return;
    const path = window.location.pathname;
    const m = path.match(/^\/(u|c)\/([^/]+)$/);
    if (m) {
      const token = m[2];
      if (m[1] === "c") {
        api.joinShare(token).then((r) => {
          window.history.replaceState({}, "", "/");
          api.listChats().then((res) => store.set({ chats: res.chats }));
          store.set({ activeChatId: r.chat_id });
        }).catch(() => 0);
      }
    }
    // Auth callback redirect (token in hash)
    if (window.location.pathname === "/auth/done" && window.location.hash.startsWith("#token=")) {
      const tok = decodeURIComponent(window.location.hash.slice("#token=".length));
      api.setToken(tok);
      window.history.replaceState({}, "", "/");
      window.location.reload();
    }
  }, [user]);

  // Subscribe to own presence WS to keep last_seen accurate
  useEffect(() => {
    if (!user) return;
    const ws = new ChatSocket("presence", user.id);
    ws.connect();
    const onlineHandler = () => api.setPresence(true, !!user.stealth).catch(() => 0);
    const offlineHandler = () => api.setPresence(false, !!user.stealth).catch(() => 0);
    document.addEventListener("visibilitychange", () => {
      document.hidden ? offlineHandler() : onlineHandler();
    });
    onlineHandler();
    return () => { ws.close(); };
  }, [user]);

  if (!ready) {
    return (
      <div className="min-h-dvh grid place-items-center">
        <div className="animate-pulse-soft"><Logo size="xl" /></div>
        {error && <div className="absolute bottom-4 text-red-300 text-sm">{error}</div>}
      </div>
    );
  }

  if (!user) {
    if (!config) return <div className="min-h-dvh grid place-items-center text-ink-300">Загрузка…</div>;
    return (
      <Login
        googleClientId={config.google_client_id}
        turnstileSiteKey={config.turnstile_site_key}
        onSuccess={async () => {
          const me = await api.me();
          store.set({ user: me.user });
          const s = await api.getSettings();
          store.set({ settings: s.settings });
          const ch = await api.listChats();
          store.set({ chats: ch.chats });
        }} />
    );
  }

  if (showSettings) {
    return (
      <div className="h-dvh flex">
        <div className="hidden md:block w-80">
          <Sidebar onPickChat={(id) => { store.set({ activeChatId: id }); setShowSettings(false); }} activeChatId={null} onOpenSettings={() => setShowSettings(true)} />
        </div>
        <div className="flex-1 relative">
          <button className="btn btn-ghost absolute top-3 left-3 z-20" onClick={() => setShowSettings(false)}>← К чатам</button>
          <SettingsPanel onLogout={() => { api.setToken(null); window.location.reload(); }} />
        </div>
      </div>
    );
  }

  return (
    <div className="h-dvh flex">
      <div className={`${activeChatId ? "hidden md:flex" : "flex"} w-full md:w-auto`}>
        <Sidebar onPickChat={(id) => store.set({ activeChatId: id })} activeChatId={activeChatId} onOpenSettings={() => setShowSettings(true)} />
      </div>
      <div className={`${activeChatId ? "flex" : "hidden md:flex"} flex-1`}>
        {activeChatId ? (
          <ChatView chatId={activeChatId} onClose={() => store.set({ activeChatId: null })} />
        ) : (
          <div className="flex-1 grid place-items-center text-center px-6">
            <div>
              <Logo size="xl" />
              <div className="mt-4 text-ink-300 max-w-md text-balance">Выберите чат слева или создайте новый. Сообщения мгновенно синхронизируются через Cloudflare Durable Objects.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
